import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Header, Segment, Card, Icon, Label, List } from "semantic-ui-react";
import { getAuthToken, getClientAud } from "../selectors";

const plans = [
  {
    key: "1m",
    months: 1,
    label: "1 Monat",
    price: 30,
    perMonth: 30,
    features: [
      "Voller Chat-Zugang für 1 Monat",
      "Automatische Verlängerung (kündbar)",
      "E-Mail-Support",
    ],
  },
  {
    key: "6m",
    months: 6,
    label: "6 Monate",
    price: 60,
    perMonth: 10,
    recommended: true,
    features: [
      "Voller Chat-Zugang für 6 Monate",
      "Spare gegenüber dem Monatsabo",
      "Priorisierter Support",
    ],
  },
  {
    key: "12m",
    months: 12,
    label: "12 Monate",
    price: 90,
    perMonth: 7.5,
    features: [
      "Voller Chat-Zugang für 12 Monate",
      "Bestes Preis‑Leistungs‑Verhältnis",
      "Priorisierter Support",
    ],
  },
];

export default function SubscriptionPage() {
  const authToken = useSelector(getAuthToken);
  const clientAud = useSelector(getClientAud);
  const [stripe, setStripe] = useState(null);
  const [loadingKey, setLoadingKey] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const mountedRef = React.useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // If the user already has an active subscription, do not show this page; go to chat
  useEffect(() => {
    async function checkStatus() {
      const res = await fetch("/api/v1/subscriptions/status", {
        headers: authToken ? {
          "Authorization": `Bearer ${authToken}`,
          "JWT_AUD": clientAud,
        } : {},
        credentials: "include",
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (!res.ok) return;
      const json = await res.json();
      if (json.active) {
        window.location.replace("/chat");
      }
    }
    checkStatus();
  }, [authToken, clientAud]);

  useEffect(() => {
    // If we arrived from Stripe success URL with session_id, confirm on backend
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    async function confirmSession() {
      const res = await fetch("/api/v1/subscriptions/confirm", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? {
            "Authorization": `Bearer ${authToken}`,
            "JWT_AUD": clientAud,
          } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (res.ok) {
        if (mountedRef.current) setConfirmed(true);
        // remove query params and redirect to chat
        window.history.replaceState({}, document.title, "/subscribe");
        window.location.href = "/chat";
      } else {
        // keep user on page and show error
        if (mountedRef.current) setError("Payment confirmation failed. Please contact support if already charged.");
      }
    }
    if (sessionId) { confirmSession(); }
  }, [authToken, clientAud]);

  useEffect(() => {
    async function loadStripe() {
      const res = await fetch("/api/v1/subscriptions/public_key", {
        headers: authToken ? {
          "Authorization": `Bearer ${authToken}`,
          "JWT_AUD": clientAud,
        } : {},
        credentials: "include",
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (!res.ok) {
        setError(`Failed to load Stripe key (${res.status})`);
        setLoadingKey(false);
        return;
      }
      const json = await res.json();
      if (!json.publishableKey) {
        setError("Stripe publishable key is not configured. Please set STRIPE_PUBLISHABLE_KEY.");
        setLoadingKey(false);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.onload = () => {
        const s = window.Stripe(json.publishableKey);
        setStripe(s);
      };
      document.body.appendChild(script);
      setLoadingKey(false);
    }
    setLoadingKey(true);
    loadStripe();
  }, [authToken, clientAud]);

  async function startCheckout(plan) {
    if (!stripe) return;
    setLoadingPlan(plan);
    const res = await fetch("/api/v1/subscriptions/checkout", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? {
          "Authorization": `Bearer ${authToken}`,
          "JWT_AUD": clientAud,
        } : {}),
      },
      credentials: "include",
      body: JSON.stringify({ plan }),
    });
    if (res.status === 401) {
      window.location.href = "/login";
      return;
    }
    if (!res.ok) {
      setError(`Checkout start failed (${res.status})`);
      setLoadingPlan(null);
      return;
    }
    const json = await res.json();
    if (json.id) await stripe.redirectToCheckout({ sessionId: json.id });
    setLoadingPlan(null);
  }

  return (
    <div className="container">
      <Header as="h2">Abonnement wählen</Header>
      <Segment>
        {error && <div style={{ color: "crimson", marginBottom: 12 }}>{error}</div>}
        <Card.Group itemsPerRow={3} stackable>
          {plans.map((p) => (
            <Card key={p.key} raised className={p.recommended ? "recommended" : ""}>
              {p.recommended && (
                <Label color="teal" ribbon style={{ zIndex: 1 }}>
                  Beliebt
                </Label>
              )}
              <Card.Content>
                <Card.Header>{p.label}</Card.Header>
                <Card.Meta>
                  <span style={{ fontSize: 24, fontWeight: 700 }}>
                    {p.price.toFixed(0)} €{" "}
                  </span>
                  <span style={{ color: "#888" }}>/ {p.months} {p.months === 1 ? "Monat" : "Monate"}</span>
                </Card.Meta>
                <Card.Description style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 14, color: "#666" }}>
                    <Icon name="calendar alternate outline" />
                    Effektiv: {p.perMonth.toFixed(2)} € / Monat
                  </div>
                  <List bulleted style={{ marginTop: 8 }}>
                    {p.features.map((f, idx) => (
                      <List.Item key={idx}>{f}</List.Item>
                    ))}
                  </List>
                </Card.Description>
              </Card.Content>
              <Card.Content extra>
                <Button
                  primary
                  fluid
                  icon
                  labelPosition="right"
                  loading={loadingKey || loadingPlan === p.key}
                  disabled={!stripe}
                  onClick={() => startCheckout(p.key)}
                >
                  Jetzt abonnieren
                  <Icon name="arrow right" />
                </Button>
              </Card.Content>
            </Card>
          ))}
        </Card.Group>
        <div style={{ marginTop: 16, color: "#666", fontSize: 12 }}>
          Mit Abschluss des Abonnements erhältst du sofortigen Chat‑Zugang. Kündigung jederzeit im Kundenportal möglich.
        </div>
      </Segment>
    </div>
  );
}

